import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '../ui/Dialog/Dialog';
import { Tabs } from '../atoms/Tabs/Tabs';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Input } from '../atoms/Input/Input';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { ManualMeasurementForm } from '../organisms/ManualMeasurementForm/ManualMeasurementForm';
import { linkMeasurement, recordAndLinkMeasurement } from '../../services/journalService';
import { listMeasurements, type MeasurementPayload } from '../../services/measurementService';
import { useTranslation } from '../../i18n/useTranslation';
import { formatMeasuredAt, measuredAtDateTimeAttr } from '../../utils/measurementDate';
import './AddMeasurementDialog.css';

export interface AddMeasurementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  aquariumId: string;
  entryId: string;
  /** Ids of measurements already linked to the entry (shown as linked). */
  linkedMeasurementIds?: string[];
  /** Called after a measurement was linked or recorded-and-linked. */
  onLinked?: () => void;
}

export const MEASUREMENTS_PAGE_SIZE = 10;

const LINK_TAB_ID = 'journal-measurement-link';
const RECORD_TAB_ID = 'journal-measurement-record';


/**
 * ONE dialog with two tabs — "Link existing" (searchable, paginated list of
 * the aquarium's measurements) and "Record new" (ManualMeasurementForm, saved
 * with the atomic POST .../measurements/record). No nested dialog.
 */
export function AddMeasurementDialog({
  isOpen,
  onClose,
  aquariumId,
  entryId,
  linkedMeasurementIds = [],
  onLinked,
}: AddMeasurementDialogProps) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(LINK_TAB_ID);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [linkError, setLinkError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(LINK_TAB_ID);
      setSearch('');
      setPage(1);
      setLinkError(false);
    }
  }, [isOpen]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['measurements', aquariumId, 'list'],
    queryFn: () => listMeasurements(aquariumId),
    enabled: isOpen && Boolean(aquariumId),
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = data ?? [];
    return term ? all.filter((m) => m.parameterName.toLowerCase().includes(term)) : all;
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / MEASUREMENTS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * MEASUREMENTS_PAGE_SIZE, currentPage * MEASUREMENTS_PAGE_SIZE);
  const linked = new Set(linkedMeasurementIds);

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
  }, []);

  const handleLink = useCallback(
    async (measurementId: string) => {
      setLinkingId(measurementId);
      setLinkError(false);
      try {
        await linkMeasurement(aquariumId, entryId, measurementId);
      } catch {
        setLinkError(true);
        return;
      } finally {
        setLinkingId(null);
      }
      onLinked?.();
      onClose();
    },
    [aquariumId, entryId, onClose, onLinked],
  );

  const recordAndLink = useCallback(
    async (payload: MeasurementPayload) => {
      const measurement = await recordAndLinkMeasurement(aquariumId, entryId, {
        parameterId: payload.parameterId,
        parameterName: payload.parameterName,
        value: payload.value,
        unit: payload.unit,
        measuredAt: payload.measuredAt,
      });
      return { ...measurement, aquariumId };
    },
    [aquariumId, entryId],
  );

  const handleRecorded = useCallback(() => {
    onLinked?.();
    onClose();
  }, [onClose, onLinked]);

  let list;
  if (isLoading) {
    list = <Text color="muted">{t('journal.measurements.dialog.loading')}</Text>;
  } else if (isError) {
    list = (
      <Alert compact>{t('journal.measurements.dialog.loadError')}</Alert>
    );
  } else if (filtered.length === 0) {
    list = (
      <Text color="muted">
        {search.trim() ? t('journal.measurements.dialog.noMatches') : t('journal.measurements.dialog.empty')}
      </Text>
    );
  } else {
    list = (
      <>
        <ul className="add-measurement-dialog__list" aria-label={t('journal.measurements.dialog.listLabel')}>
          {pageItems.map((m) => {
            const isLinked = linked.has(m.id);
            return (
              <li key={m.id}>
                <Card padding="sm" className="add-measurement-dialog__item">
                  <span className="add-measurement-dialog__item-text">
                    <Text as="span" weight="semibold">
                      {m.parameterName}: {m.value} {m.unit}
                    </Text>
                    <Text as="span" size="sm" color="secondary">
                      <time dateTime={measuredAtDateTimeAttr(m)}>{formatMeasuredAt(m, i18n.language, 'short')}</time>
                    </Text>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant={isLinked ? 'ghost' : 'secondary'}
                    disabled={isLinked || linkingId !== null}
                    isLoading={linkingId === m.id}
                    aria-label={
                      isLinked
                        ? t('journal.measurements.dialog.alreadyLinkedLabel', { parameter: m.parameterName, date: formatMeasuredAt(m, i18n.language, 'short') })
                        : t('journal.measurements.dialog.linkLabel', { parameter: m.parameterName, date: formatMeasuredAt(m, i18n.language, 'short') })
                    }
                    onClick={() => void handleLink(m.id)}
                  >
                    {isLinked ? t('journal.measurements.dialog.alreadyLinked') : t('journal.measurements.dialog.linkAction')}
                  </Button>
                </Card>
              </li>
            );
          })}
        </ul>
        {totalPages > 1 && (
          <nav className="add-measurement-dialog__pagination" aria-label={t('journal.measurements.dialog.pagination')}>
            <Button type="button" variant="ghost" size="sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
              {t('journal.previous')}
            </Button>
            <Text as="span" size="sm">
              {t('journal.pageOf', { page: currentPage, total: totalPages })}
            </Text>
            <Button type="button" variant="ghost" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
              {t('journal.next')}
            </Button>
          </nav>
        )}
      </>
    );
  }

  const linkPanel = (
    <div className="add-measurement-dialog__panel">
      <Input
        id="journal-measurement-search"
        type="search"
        label={t('journal.measurements.dialog.searchLabel')}
        value={search}
        onChange={handleSearch}
      />
      {list}
      {linkError && <Alert compact>{t('journal.measurements.dialog.linkError')}</Alert>}
    </div>
  );

  const recordPanel = (
    <div className="add-measurement-dialog__panel">
      {isOpen && activeTab === RECORD_TAB_ID && (
        <ManualMeasurementForm aquariumId={aquariumId} submit={recordAndLink} onSuccess={handleRecorded} />
      )}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('journal.measurements.dialog.title')}
      size="lg"
      data-testid="add-measurement-dialog"
    >
      <Tabs
        aria-label={t('journal.measurements.dialog.tabsLabel')}
        activeId={activeTab}
        onChange={setActiveTab}
        items={[
          { id: LINK_TAB_ID, label: t('journal.measurements.dialog.linkTab'), panel: linkPanel },
          { id: RECORD_TAB_ID, label: t('journal.measurements.dialog.recordTab'), panel: recordPanel },
        ]}
      />
    </Dialog>
  );
}
